import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { Event, FileAsset, ImageAsset } from '@rive-app/react-canvas';
import { Alignment, EventType, Fit, Layout } from '@rive-app/react-canvas';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import dailyBonusRiv from '../../../../assets/daily-bonus/daily_bonus.riv';
import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { useCoinsAnimation } from '../../../../hooks/useCoinsAnimation';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import type { ClaimableReward } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';
import { devError } from '../../../../utils/log';

import { ARTBOARD_NAME, STATE_MACHINE_NAME } from './constants';
import styles from './DailyBonus.module.scss';
import type { RiveEventPayload } from './dailyBonusRive.types';
import { buildReferencedAssets, delay, loadRiveImage } from './dailyBonusRiveHelpers';
import { useDailyBonusRiveBindings } from './useDailyBonusRiveBindings';

const DailyBonusNewDesign = observer(function DailyBonusNewDesign() {
  const { portalStore, rewardsStore, featureManagerStore, loyaltyProgramStore } = useStores();
  const { t } = useTranslation();
  const { startAnimationAsync, startQueuedMiniGamesAsync } = useCoinsAnimation();
  const isScModeAvailable = useFlag<boolean>('sc_mode_available', true);
  const isClaimingRef = useRef(false);

  const referencedAssets = useMemo(
    () => buildReferencedAssets(rewardsStore.dailyBonusesList, isScModeAvailable, t),
    [rewardsStore.dailyBonusesList, isScModeAvailable, t],
  );

  const assetLoader = useCallback(
    (asset: FileAsset, _bytes: Uint8Array) => {
      const url = asset.isImage && referencedAssets[asset.name];
      if (!url) return false;
      void loadRiveImage(url, asset as ImageAsset);
      return true;
    },
    [referencedAssets],
  );

  const { rive, RiveComponent } = useRiveMachine({
    src: dailyBonusRiv,
    artboard: ARTBOARD_NAME,
    stateMachineName: STATE_MACHINE_NAME,
    autoplay: false,
    autoBind: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.BottomCenter,
    }),
    assetLoader,
  });

  const { fireClaim, checkAndFireMonthlyBonus } = useDailyBonusRiveBindings(rive, t, isScModeAvailable);

  useEffect(() => {
    void analytics.sendEvent('daily_bonus', {
      event_category: 'daily_bonus',
      event_action: 'impression',
    });
  }, []);

  const close = useCallback(() => {
    void analytics.sendEvent('daily_bonus', {
      event_category: 'daily_bonus',
      event_action: 'close',
      click_location: 'close',
    });
    isClaimingRef.current = false;
    portalStore.closePortal({ preventBeforeClose: true });
    featureManagerStore.onDialogFinished('DailyBonus');
  }, [portalStore, featureManagerStore]);

  const handleClaim = useCallback(async () => {
    if (isClaimingRef.current) return;

    const dailyBonus = rewardsStore.dailyBonus;

    if (!dailyBonus?.isEntitledToBonus) {
      close();
      return;
    }

    isClaimingRef.current = true;

    const currentClaimDay = dailyBonus?.currentDay || 0;
    const currentMonthlyDay = dailyBonus?.monthlyBonusDay || 0;
    const claimDayPackageId = rewardsStore.dailyBonusesList.find((b) => b.day === currentClaimDay + 1)?.packageId;

    fireClaim();

    try {
      const response = await rewardsStore.claimDailyBonusV2();
      if (!response?.data) return;

      void analytics.sendEvent('claim_daily_bonus', {
        event_category: 'daily_bonus',
        event_type: 'day_number',
        event_value: currentClaimDay + 1,
        event_action: 'click',
        cc_amount: response.data.gc_amount,
        sc_amount: response.data.sc_amount,
        package_id: claimDayPackageId,
        tag: loyaltyProgramStore.userLoyalty?.currentTier?.key,
      });

      const dailyBundleItems = response.data.dailyPackageData || [];
      const monthlyData = response.data.monthlyPackageData;
      const isMonthlyDataObject = !!monthlyData && !Array.isArray(monthlyData);
      const monthlyBundleItems = isMonthlyDataObject ? monthlyData.packageBundles : [];
      const monthlyScAmount = isMonthlyDataObject ? (monthlyData.sc_amount ?? 0) : 0;
      const monthlyGcAmount = isMonthlyDataObject ? (monthlyData.gc_amount ?? 0) : 0;
      const hasMonthlyBonus = monthlyBundleItems.length > 0 || isMonthlyDataObject;

      const dailyRewards: ClaimableReward[] = [
        {
          scCoin: isScModeAvailable ? response.data.sc_amount : 0,
          gcCoin: response.data.gc_amount,
          bundleItems: monthlyBundleItems,
        },
      ];

      await delay(500);
      await startAnimationAsync({ rewards: dailyRewards, startMiniGames: false });

      if (hasMonthlyBonus) {
        const newMonthlyDay = currentMonthlyDay + 1;
        await delay(500);
        checkAndFireMonthlyBonus(newMonthlyDay);
        await delay(1500);

        const monthlyRewards: ClaimableReward[] = [
          {
            scCoin: isScModeAvailable ? monthlyScAmount : 0,
            gcCoin: monthlyGcAmount,
            bundleItems: monthlyBundleItems,
          },
        ];

        await startAnimationAsync({ rewards: monthlyRewards, startMiniGames: false });
      }

      isClaimingRef.current = false;
      close();

      await startQueuedMiniGamesAsync([...dailyBundleItems, ...monthlyBundleItems]);
    } catch (e) {
      devError('Error claiming daily bonus (new design)', e);
    }
  }, [
    close,
    rewardsStore,
    fireClaim,
    checkAndFireMonthlyBonus,
    startAnimationAsync,
    startQueuedMiniGamesAsync,
    loyaltyProgramStore,
    isScModeAvailable,
  ]);

  useEffect(() => {
    if (!rive) return;

    const onRiveEvent = (event: Event) => {
      const eventData = event?.data as RiveEventPayload;
      const eventName = eventData?.name;
      if (!eventName) return;

      if (eventName === 'day_claim') {
        void handleClaim();
      }
      if (eventName === 'close') {
        close();
      }
    };

    rive.on(EventType.RiveEvent, onRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, onRiveEvent);
    };
  }, [rive, handleClaim, close]);

  return (
    <div className={styles.container}>
      <div className={styles.riveWrapper}>
        <RiveComponent className={styles.rive} />
      </div>
    </div>
  );
});

export default DailyBonusNewDesign;
